// frontend/src/components/BannerApp.tsx
// Banner de invitación a instalar la app nativa SGCC (fase 0 de convivencia
// PWA ↔ Expo, plan de migración §3.1 ítem 3).
//
// Reglas de visibilidad:
//  - Apagado por completo mientras no exista VITE_APP_STORE_URL ni
//    VITE_PLAY_STORE_URL en el build (hoy no existen: no se renderiza nada).
//  - Solo con sesión iniciada (App lo monta dentro del layout autenticado).
//  - Descartable por 7 días (localStorage sgcc_banner_app_oculto_hasta).
//  - Con pendientes = 0 y sin marcas en vuelo: "Ya está disponible la app SGCC"
//    + botón al store de la plataforma.
//  - Con pendientes > 0: "Sincronizá tus N marcas antes de instalar la app"
//    + botón que dispara sincronizar(). Nunca se invita a instalar con datos
//    sin subir: la app nativa no puede leer IndexedDB.
//  - Con marcas en vuelo (cuenta regresiva / geolocalización) no se muestra.
//
// Reactivo a `sgcc:sync-data-changed` (vía pendingCount de useOfflineSync) y a
// los cambios del contador de marcas en vuelo.

import { useState, useEffect } from 'react';
import { Smartphone, CloudUpload, X } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { getMarcasEnVuelo, suscribirMarcasEnVuelo } from '../lib/marcasEnVuelo';

const OCULTO_HASTA_KEY = 'sgcc_banner_app_oculto_hasta';
const DIAS_OCULTO = 7;

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL || '';
const PLAY_STORE_URL = import.meta.env.VITE_PLAY_STORE_URL || '';

type Plataforma = 'ios' | 'android' | 'otra';

function detectarPlataforma(): Plataforma {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ se presenta como Mac: distinguir por touch
  const esIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (esIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'otra';
}

interface EnlaceStore {
  url: string;
  etiqueta: string;
}

// Enlaces a mostrar según plataforma: iOS → App Store, Android → Play; en
// escritorio se ofrecen los que existan. Vacío si no hay enlace útil.
function enlacesParaPlataforma(plataforma: Plataforma): EnlaceStore[] {
  const appStore: EnlaceStore | null = APP_STORE_URL ? { url: APP_STORE_URL, etiqueta: 'Abrir App Store' } : null;
  const play: EnlaceStore | null = PLAY_STORE_URL ? { url: PLAY_STORE_URL, etiqueta: 'Abrir Google Play' } : null;
  if (plataforma === 'ios') return appStore ? [appStore] : [];
  if (plataforma === 'android') return play ? [play] : [];
  return [appStore, play].filter((e): e is EnlaceStore => e !== null);
}

function leerOcultoHasta(): number {
  try {
    const valor = localStorage.getItem(OCULTO_HASTA_KEY);
    if (!valor) return 0;
    const ts = Date.parse(valor);
    return Number.isNaN(ts) ? 0 : ts;
  } catch {
    return 0;
  }
}

export function BannerApp() {
  const { pendingCount, isSyncing, isOnline, sincronizar } = useOfflineSync();
  const [ocultoHasta, setOcultoHasta] = useState<number>(leerOcultoHasta);
  const [marcasEnVuelo, setMarcasEnVuelo] = useState<number>(getMarcasEnVuelo);

  useEffect(() => suscribirMarcasEnVuelo(() => setMarcasEnVuelo(getMarcasEnVuelo())), []);

  // Sin enlaces configurados el banner está apagado (estado actual de producción)
  const enlaces = enlacesParaPlataforma(detectarPlataforma());
  if (enlaces.length === 0) return null;
  if (ocultoHasta > Date.now()) return null;
  if (marcasEnVuelo > 0) return null;

  const descartar = () => {
    const hasta = new Date(Date.now() + DIAS_OCULTO * 24 * 60 * 60 * 1000);
    try {
      localStorage.setItem(OCULTO_HASTA_KEY, hasta.toISOString());
    } catch {
      // localStorage inaccesible: se oculta solo durante esta sesión
    }
    setOcultoHasta(hasta.getTime());
  };

  const hayPendientes = pendingCount > 0;

  return (
    <div
      role="status"
      className="flex items-start gap-3 px-4 py-3 border-b animate-fade-in"
      style={{
        backgroundColor: hayPendientes
          ? 'var(--color-sgcc-secondary-fixed)'
          : 'var(--color-sgcc-primary-fixed)',
        borderColor: 'var(--color-sgcc-outline-variant)',
        color: hayPendientes
          ? 'var(--color-sgcc-on-secondary-container)'
          : 'var(--color-sgcc-primary)',
      }}
    >
      <div
        className="w-9 h-9 shrink-0 rounded-[10px] flex items-center justify-center"
        style={{
          backgroundColor: hayPendientes ? 'var(--color-sgcc-secondary-container)' : 'var(--color-sgcc-primary)',
          color: hayPendientes ? 'var(--color-sgcc-on-secondary-container)' : 'var(--color-sgcc-on-primary)',
        }}
      >
        {hayPendientes ? <CloudUpload size={18} strokeWidth={2} /> : <Smartphone size={18} strokeWidth={2} />}
      </div>

      <div className="flex-1 min-w-0">
        {hayPendientes ? (
          <>
            <p className="text-ios-subhead font-semibold leading-tight">
              Sincronizá tus {pendingCount} {pendingCount === 1 ? 'marca' : 'marcas'} antes de instalar la app
            </p>
            <p className="text-ios-caption1 mt-0.5 opacity-80">
              La app nueva no puede leer lo guardado en esta versión web.
            </p>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1.5 px-3 h-8 rounded-[10px] text-ios-footnote font-semibold tap-active disabled:opacity-50"
              style={{
                backgroundColor: 'var(--color-sgcc-primary)',
                color: 'var(--color-sgcc-on-primary)',
              }}
              disabled={isSyncing || !isOnline}
              onClick={() => { void sincronizar(); }}
            >
              <CloudUpload size={14} strokeWidth={2} />
              {isSyncing ? 'Sincronizando…' : isOnline ? 'Sincronizar ahora' : 'Sin conexión'}
            </button>
          </>
        ) : (
          <>
            <p className="text-ios-subhead font-semibold leading-tight">Ya está disponible la app SGCC</p>
            <p className="text-ios-caption1 mt-0.5 opacity-80">
              Instalala desde la tienda para seguir marcando presentismo.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {enlaces.map((enlace) => (
                <a
                  key={enlace.url}
                  href={enlace.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-[10px] text-ios-footnote font-semibold tap-active"
                  style={{
                    backgroundColor: 'var(--color-sgcc-primary)',
                    color: 'var(--color-sgcc-on-primary)',
                  }}
                >
                  <Smartphone size={14} strokeWidth={2} />
                  {enlace.etiqueta}
                </a>
              ))}
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        className="w-8 h-8 shrink-0 -mr-2 -mt-1 rounded-full flex items-center justify-center tap-active"
        style={{ color: 'inherit', opacity: 0.7 }}
        aria-label="Ocultar por 7 días"
        onClick={descartar}
      >
        <X size={18} strokeWidth={2} />
      </button>
    </div>
  );
}
